import Animation from 'animation'
import * as scale from 'd3-scale'
import * as shape from 'd3-shape'
import * as array from 'd3-array'
import * as d3 from 'd3-selection'
import * as colors from 'colors'
import { EPSILON } from 'chart-constants'

class ReferenceBandAnimation extends Animation {

  configure (axis = 'left', serie) {
    super.configure(axis, serie)

    // Validate the serie data
    let thresholds = serie.points.find(p => p.thresholds && p.thresholds.length).thresholds
    if (serie.fills && serie.fills.length != thresholds.length + 1) {
      throw new Error('Fills array must equal thresholds.length + 1')
    }

    // check if this is a no-breakdown chart
    if (this.chart.layout.breakdown.calculated) {
      // The points won't have a rangeId. Populate the range id of each point
      let range = this.chart.layout.breakdown.range
      serie.points.forEach((point, i) => {
        point.rangeId = range[i].id
      })
    }

    this.line = shape.line().defined(d => !isNaN(d[1]))
    this.area = shape.area().y0(d => d[2]).defined(d => {
      // both values must be numbers
      return !isNaN(d[1]) && !isNaN(d[2])
    })

    // Number of threshold lines
    this.totalThresholds = array.max(serie.points, p => p.thresholds.length)
    this.seriesRefIds = new Set(serie.points.map(p => p.seriesRefId).filter(d => d != undefined))

    // Clone the normal groupScale
    this.groupScale = this.scalers.x.groupScale.copy()

    this.splitBySerie = false

    if (this.seriesRefIds.size) {
      let domain = Array.from(this.seriesRefIds)
        , columnIdxMap = this.chart.layout.getColumnsByAxis()

      // See https://github.com/SpiderStrategies/Scoreboard/issues/42635
      // We need to pad the domain for columnIdx series on the opposing axis
      // if the breakdown is NOT calculated.
      // If it's a calculated breakdown, the series will be tied exactly to a
      // range. See https://github.com/SpiderStrategies/Scoreboard/issues/45868
      if (!this.chart.layout.breakdown.calculated) {
        if (axis === 'left') {
          domain = domain.concat(...columnIdxMap.right.map(i => `${axis}-column-idx-${i}`))
        } else if (axis === 'right') {
          domain = columnIdxMap.right.map(i => `${axis}-column-idx-${i}`).concat(...domain)
        }
      }

      this.refSerieScale = scale.scaleBand()
                                .domain(domain)
                                .range(this.serieScale.range())
                                .paddingInner(0) // Inner reference bands touch
                                .paddingOuter(this.serieScale.paddingInner()) // Use the previous inner padding, so the lines extend properly on the outside. See Impact #40520

      this.splitBySerie = true
    } else {
      // Remove the padding between the x-axis items so bands always touch
      this.groupScale.padding(0) // No gaps between the groups

      let plotDomain = this.scalers.x.plotScale.domain()

      // When we're showing multiple plots (i.e 2 levels of breakdown), and
      // the scope is 'entire chart', we need to make the reference band look
      // continuous across all plots. That's tricky because of how plots
      // are separated and we use clip paths
      if (serie.scope === 'entire-chart' && this.plotId !== plotDomain[plotDomain.length - 1]) {
        // If it's not the last plot, set the group scale so it spans the entire
        // plot
        this.groupScale.range([0, this.scalers.x.plotScale.step()])
      }

      // Continuous across the group
      this.refSerieScale = scale.scaleBand()
                                .domain([undefined, undefined]) // Mock a domain with undefined values
                                .range([0, this.groupScale.bandwidth()]) // Will stretch across the entire group
                                .padding(0)
    }

    this.serie = serie
    this.fills = (serie.fills || []).slice().reverse() // reverse the order so they are drawn top-down
    this.lines = serie.lines || []

    if (this.series.node()) {
      this.previousRefSerieScale = this.series.selection()
                                              .filter(s => s.id == serie.id)
                                              .node().__refSerieScale
    }

    return this
  }

  _getPointByGroup (group) {
    return this.serie.points.find(point => {
      return point.rangeId == group.id && point.seriesRefId == group.seriesRefId
    })
  }

  _getXExtent (rangeId, seriesRefId) {
    let x0 = this.groupScale(rangeId) + this.refSerieScale(seriesRefId)
      , x1 = this.groupScale(rangeId) + this.refSerieScale(seriesRefId) + this.refSerieScale.bandwidth()

    return [x0, x1]
  }

  /*
   * Animations can override this if the starting location of the
   * elements needs to change
   */
  _getStartXExent (rangeId, seriesRefId) {
    return this._getXExtent(rangeId, seriesRefId)
  }

  bindAndAnimate (container, node) {
    let serie = d3.select(node).datum()
      , svg = d3.select(node.ownerSVGElement)
      , group = d3.select(node)
                  .selectAll('.reference-group')
                  .data(d => [d])
      , enter = group.enter()
                     .append('g')
                     .attr('class', 'reference-group')
      , range = this.chart.layout.breakdown.range

    // We want to build a `reference-breakdown` for every item in the defined
    // range
    if (!range.length) {
      // No breakdown defined. This reference band expands the whole chart
      range = [null]
    }

    range = range.map(r => {
      if (!this.seriesRefIds.size) {
        return r
      }
      return Array.from(this.seriesRefIds).map(id => {
        return Object.assign({}, r, {
          seriesRefId: id
        })
      })
    }).flat()

    // Create the groups for each item in the x-axis
    // Build a group of elements for each item in our range
    let referenceGroups = group.merge(enter)
                               .attr('data-split-by-serie', this.splitBySerie) // Store so animations can figure out if it was previously split
                               .attr('clip-path', `url(#${svg.attr('id')}-chart-clip-path-plot-height-${this.plotId})`)
                               .selectAll('g.reference-breakdown')
                               .data(range, d => `${d.id}-${d.seriesRefId || ''}`)
      , refGroupEnter = referenceGroups.enter()
                                        .append('g')
                                        .attr('class', 'reference-breakdown')
      , refExit = referenceGroups.exit().transition(container)
      , transition = refGroupEnter.merge(referenceGroups)
                                  .transition(container)

    this.animate(serie, refGroupEnter, referenceGroups, transition, refExit)

    if (this.series.node()) {
      let node = this.series.selection()
                            .filter(s => s.id == serie.id)
                            .node()
      node.__refSerieScale = this.refSerieScale
    }
  }

  _lines (selection, transition, yAccessor0) {
    if (!yAccessor0) {
      let yscale = this.previous.yScale || this.yScale
      // By default the y accessor uses the raw value on the point
      yAccessor0 = point => yscale(point.raw)
    }

    let lines = selection.selectAll('path.line')
                         .data(d => {
                           return [...Array(this.totalThresholds)].map((_, i) => {
                             let point = this._getPointByGroup(d)
                               , line = this.lines[i]

                             if (!point || !point.thresholds.length) {
                               return null
                             }

                             return {
                               color: line ? line.color : null,
                               opacity: line ? (line.opacity || 1) : EPSILON, // If the line doesn't have a color we don't show it, but keep it in the DOM.
                               raw: point.thresholds[i].raw,
                               rangeId: point.rangeId,
                               seriesRefId: d.seriesRefId
                             }
                           }).filter(Boolean)
                         })

    lines.enter()
         .append('path')
         .attr('d', d => {
           let [x0, x1] = this._getStartXExent(d.rangeId, d.seriesRefId)
             , y = yAccessor0(d)
           return this.line([[x0, y], [x1, y]])
         })
         .style('opacity', EPSILON)
         .merge(lines)
         .attr('class', d => `line ${d.color}`)
         .style('stroke', d => colors.lookup(d.color))
         .transition(transition)
         .attr('d', d => {
           let [x0, x1] = this._getXExtent(d.rangeId, d.seriesRefId)
             , y = this.yScale(d.raw)

           return this.line([[x0, y], [x1, y]])
         })
         .style('opacity', d => d.opacity)

     lines.exit()
          .transition(transition)
          .style('opacity', EPSILON)
           .attr('d', d => {
             let [x0, x1] = this._getXExtent(d.rangeId, d.seriesRefId)
              , y = this.yScale(d.raw)

             return this.line([[x0, y], [x1, y]])
           })
           .remove()
  }

  _getBounds (point, idx, all) {
    // Sort thresholds in ascending order
    let thresholds = point.thresholds.slice().sort((a, b) => {
        return a.raw - b.raw
      })
      , bottom = null
      , top = null

    if (idx === all.length - 1) {
      // Top fill
      top = array.max(this.yScale.domain())
      bottom = thresholds[idx - 1].raw
    } else if (idx === 0) {
      // Lowest fill
      bottom = array.min(this.yScale.domain())
      top = thresholds[0].raw
    } else {
      top = thresholds[idx].raw
      bottom = thresholds[idx - 1].raw
    }

    return [ bottom, top ]
  }

  _fills (selection, transition, yAccessor0) {
    if (!yAccessor0) {
      // The yAccessor0 by default uses the bounds on the point.
      // This can be overridden if the starting values need to change
      let yscale = this.previous.yScale || this.yScale
      yAccessor0 = point => {
        let lower = yscale(point.bounds[0])
          , upper = yscale(point.bounds[1])

        return [lower, upper]
      }
    }

    let fills = selection.selectAll('path.fill')
                         .data(d => {
                           if (!this.fills.length) {
                             return [] // No fills
                           }

                           return this.fills.map((fill, i, all) => {
                             if (!fill) {
                               // placeholder fill
                               return null
                             }

                             let point = this._getPointByGroup(d)

                             if (!point) {
                              return null
                             }

                             return {
                               color: fill.color,
                               opacity: fill.opacity || 1,
                               idx: i,
                               bounds: this._getBounds(point, i, all),
                               rangeId: point.rangeId,
                               seriesRefId: d.seriesRefId
                             }
                           }).filter(Boolean)
                         }, d => d.idx)

    let enter = fills.enter()
                     .append('path')
                       .attr('class', d => `fill ${d.color}`)
                       .style('fill', d => colors.lookup(d.color))
                       .style('opacity', EPSILON)
                       .attr('d', d => {
                         // Always start at the previous yscale
                         let [x0, x1] = this._getStartXExent(d.rangeId, d.seriesRefId)
                           , [y0, y1] = yAccessor0(d)

                         return this.area([[x0, y1, y0], [x1, y1, y0]])
                       })

    fills.exit()
         .transition(transition)
         .style('opacity', EPSILON)
         .attr('d', d => {
           let [x0, x1] = this._getXExtent(d.rangeId, d.seriesRefId)
             , y0 = this.yScale(d.bounds[0])
             , y1 = this.yScale(d.bounds[1])

           return this.area([[x0, y1, y0], [x1, y1, y0]])
         })
         .remove()

    fills.merge(enter)
         .transition(transition)
         .attr('d', d => {
           let [x0, x1] = this._getXExtent(d.rangeId, d.seriesRefId)
             , y0 = this.yScale(d.bounds[0])
             , y1 = this.yScale(d.bounds[1])

           return this.area([[x0, y1, y0], [x1, y1, y0]])
         })
         .attr('class', d => `fill ${d.color}`)
         .attr('fill', d => colors.lookup(d.color))
         .style('opacity', d => d.opacity)
  }

  animate (serie, enter, selection, merged, exit) {
    this._fills(merged.selection(), merged)
    this._lines(merged.selection(), merged)
  }

}

export default ReferenceBandAnimation
